import React, { useState, useEffect, useRef } from 'react';
import { softDelete, softDeleteHijos } from '../utils/softDelete';
import { supabase } from '../supabase';
import TramiteDetalle from './TramiteDetalle';
import { useEsAdmin } from '../UserContext';
import { emitEntidadChanged, onEntidadChanged } from '../utils/entidadBus';
import { onDataChanged } from '../utils/dataBus';

export default function Tramites() {
  const esAdmin = useEsAdmin();
  const mouseDownPos = useRef(null);
  const [tramites, setTramites] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [orden, setOrden] = useState('mas_nuevos');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [modalConfirmOpen, setModalConfirmOpen] = useState(null);
  const [toast, setToast] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tramiteSeleccionado, setTramiteSeleccionado] = useState(null);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [dropdownClienteOpen, setDropdownClienteOpen] = useState(false);

  const [form, setForm] = useState({
    nombre: '', tipo: '', fecha_inicio: '', fecha_vencimiento: '',
    estado: 'pendiente', parte_contraria: '', resultado: '',
    expediente_id: '', cliente_ids: [],
    mostrar_tipo: true, mostrar_parte_contraria: true
  });

  useEffect(() => { fetchTramites(); fetchClientes(); fetchExpedientes(); }, []);

  // Refrescar si un cliente o expediente vinculado cambia desde otro lugar
  useEffect(() => onEntidadChanged((d) => {
    if (d.tipo === 'cliente') fetchClientes();
    if (d.tipo === 'expediente') { fetchExpedientes(); fetchTramites(); }
  }), []);

  // Automático: cualquier escritura sobre trámites, expedientes o clientes
  useEffect(() => onDataChanged(() => { fetchTramites(); fetchClientes(); fetchExpedientes(); }, ['tramites', 'expedientes', 'clientes', 'tramites_clientes']), []);

  async function fetchTramites() {
    setLoading(true);
    const { data } = await supabase
      .from('tramites')
      .select('*, tramites_clientes(cliente_id, clientes(nombre, apellido)), expedientes(numero, caratula)')
      .eq('eliminado', false)
      .order('created_at', { ascending: false });
    setTramites(data || []);
    setLoading(false);
  }

  async function fetchClientes() {
    const { data } = await supabase.from('clientes').select('id, nombre, apellido').eq('eliminado', false).order('nombre');
    setClientes(data || []);
  }

  async function fetchExpedientes() {
    const { data } = await supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula');
    setExpedientes(data || []);
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  function openDrawer(t) { setTramiteSeleccionado(t.id); setDrawerOpen(true); }

  function openNew() {
    setEditingId(null);
    setForm({
      nombre: '', tipo: '', fecha_inicio: new Date().toISOString().split('T')[0],
      fecha_vencimiento: '', estado: 'pendiente',
      parte_contraria: '', resultado: '', notas: '',
      expediente_id: '', cliente_ids: [],
      mostrar_tipo: true, mostrar_parte_contraria: true
    });
    setModalOpen(true);
  }

  function openEdit(t, e) {
    e.stopPropagation();
    setEditingId(t.id);
    setForm({
      nombre: t.nombre || '', tipo: t.tipo || '',
      fecha_inicio: t.fecha_inicio || '', fecha_vencimiento: t.fecha_vencimiento || '',
      estado: t.estado || 'pendiente', parte_contraria: t.parte_contraria || '',
      resultado: t.resultado || '', notas: t.notas || '',
      expediente_id: t.expediente_id || '',
      cliente_ids: t.tramites_clientes?.map(tc => tc.cliente_id) || [],
      mostrar_tipo: !!t.tipo, mostrar_parte_contraria: !!t.parte_contraria
    });
    setModalOpen(true);
  }

  async function saveTramite() {
    if (guardando) return;
    if (!form.nombre.trim()) { showToast('El nombre es obligatorio'); return; }
    setGuardando(true);
    const tramiteData = {
      nombre: form.nombre, tipo: form.mostrar_tipo ? form.tipo : null,
      fecha_inicio: form.fecha_inicio || null, fecha_vencimiento: form.fecha_vencimiento || null,
      estado: form.estado,
      parte_contraria: form.mostrar_parte_contraria ? form.parte_contraria : null,
      resultado: form.estado === 'completado' ? form.resultado : null,
      expediente_id: form.expediente_id || null
    };
    let tramiteId = editingId;
    if (editingId) {
      await supabase.from('tramites').update(tramiteData).eq('id', editingId);
      await supabase.from('tramites_clientes').delete().eq('tramite_id', editingId);
    } else {
      const result = await supabase.from('tramites').insert(tramiteData).select('id').single();
      if (!result.data || result.error) { showToast('Error al guardar'); return; }
      tramiteId = result.data.id;
    }
    if (form.cliente_ids.length > 0) {
      await supabase.from('tramites_clientes').insert(form.cliente_ids.map(cid => ({ tramite_id: tramiteId, cliente_id: cid })));
    }
    showToast(editingId ? 'Trámite actualizado ✓' : 'Trámite creado ✓');
    setGuardando(false);
    setModalOpen(false);
    fetchTramites();
  }

  async function deleteTramite(id, e) {
    e.stopPropagation();
    const tramite = tramites.find(t => t.id === id);
    setModalConfirmOpen({ id, nombre: tramite?.nombre || 'este trámite' });
  }

  async function confirmarAccionTramite(accion, id) {
    setModalConfirmOpen(null);
    if (accion === 'completado' || accion === 'archivado') {
      await supabase.from('tramites').update({ estado: accion }).eq('id', id);
      showToast(accion === 'completado' ? 'Trámite marcado como completado ✓' : 'Trámite archivado ✓');
      fetchTramites();
    } else if (accion === 'eliminar') {
      await supabase.from('tramites_clientes').delete().eq('tramite_id', id);
      await Promise.all([
        softDeleteHijos('tareas', 'tramite_id', id),
        softDeleteHijos('agenda', 'tramite_id', id),
        softDeleteHijos('tramites_notas', 'tramite_id', id),
      ]);
      const { error } = await softDelete('tramites', id);
      if (error) { showToast('Error al eliminar: ' + error.message); return; }
      showToast('Trámite enviado a la papelera');
      emitEntidadChanged({ tipo: 'tramite', id, eliminado: true });
      fetchTramites();
    }
    fetchTramites();
  }

  function toggleCliente(id) {
    setForm(f => ({
      ...f,
      cliente_ids: f.cliente_ids.includes(id) ? f.cliente_ids.filter(c => c !== id) : [...f.cliente_ids, id]
    }));
  }

  const filtered = tramites.filter(t => {
    const s = search.toLowerCase();
    const matchSearch = !s || t.nombre?.toLowerCase().includes(s) || t.tipo?.toLowerCase().includes(s) ||
      t.tramites_clientes?.some(tc => `${tc.clientes?.nombre} ${tc.clientes?.apellido}`.toLowerCase().includes(s));
    const matchEstado = !filtroEstado || t.estado === filtroEstado;
    return matchSearch && matchEstado;
  }).sort((a, b) => {
    switch (orden) {
      case 'alfabetico':       return (a.nombre || '').localeCompare(b.nombre || '', 'es');
      case 'alfabetico_inv':   return (b.nombre || '').localeCompare(a.nombre || '', 'es');
      case 'mas_nuevos':       return (b.created_at || '').localeCompare(a.created_at || '');
      case 'mas_antiguos':     return (a.created_at || '').localeCompare(b.created_at || '');
      case 'venc_proximo':     return (a.fecha_vencimiento || '9999').localeCompare(b.fecha_vencimiento || '9999');
      case 'venc_lejano':      return (b.fecha_vencimiento || '').localeCompare(a.fecha_vencimiento || '');
      default:                 return 0;
    }
  });

  const estadoBadge = (e) => {
    const map = { pendiente: 'badge-amber', 'en espera': 'badge-blue', completado: 'badge-green', archivado: 'badge-gray' };
    return <span className={`badge ${map[e] || 'badge-gray'}`}>{e}</span>;
  };

  const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';

  return (
    <div className="page">
      <div className="page-topbar">
        <div className="flex" style={{ alignItems: 'center' }}>
          <span className="page-title">Trámites extrajudiciales</span>
          <span className="page-subtitle">— {filtered.length} trámite{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Nuevo trámite</button>
      </div>

      <div className="page-content">
        <div className="toolbar">
          <div className="search-box">
            <span style={{ color: 'var(--text4)' }}>⌕</span>
            <input placeholder="Buscar por nombre, tipo, cliente..." value={search} onChange={e => setSearch(e.target.value)} autoComplete="off" spellCheck={false} />
            {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: 'var(--text4)', cursor: 'pointer', padding: '0 2px', fontSize: 16, lineHeight: 1, flexShrink: 0 }}>×</button>}
          </div>
          <select className="filter-select" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="en espera">En espera</option>
            <option value="completado">Completado</option>
            <option value="archivado">Archivado</option>
          </select>
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 12, color: 'var(--text4)', fontWeight: 500 }}>Ordenar por:</span>
            <select className="filter-select" value={orden} onChange={e => setOrden(e.target.value)} style={{ fontSize: 12, minWidth: 240 }}>
              <option value="alfabetico">Alfabético (A → Z)</option>
              <option value="alfabetico_inv">Alfabético (Z → A)</option>
              <option value="mas_nuevos">Creación (más recientes)</option>
              <option value="mas_antiguos">Creación (más antiguos)</option>
              <option value="venc_proximo">Vencimiento (más próximo)</option>
              <option value="venc_lejano">Vencimiento (más lejano)</option>
            </select>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Clientes</th>
                  <th>Tipo</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="3" style={{ textAlign: 'center', padding: 40, color: 'var(--text4)' }}>Cargando...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="3">
                    <div className="empty-state">
                      <div className="empty-state-icon">📋</div>
                      <div className="empty-state-title">No hay trámites</div>
                    </div>
                  </td></tr>
                ) : filtered.map(t => (
                  <tr key={t.id} style={{ cursor: 'pointer' }}
                    onMouseDown={ev => { mouseDownPos.current = { x: ev.clientX, y: ev.clientY }; }}
                    onClick={ev => {
                      if (mouseDownPos.current) {
                        const dx = Math.abs(ev.clientX - mouseDownPos.current.x);
                        const dy = Math.abs(ev.clientY - mouseDownPos.current.y);
                        if (dx > 5 || dy > 5) return;
                      }
                      openDrawer(t);
                    }}>
                    <td className="td-primary">{t.nombre}</td>
                    <td style={{ fontSize: 12 }}>{t.tramites_clientes?.map(tc => [tc.clientes?.apellido, tc.clientes?.nombre].filter(Boolean).join(', ')).join(' / ') || '—'}</td>
                    <td>{t.tipo ? <span className="badge badge-gray">{t.tipo}</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <>
          <div className="drawer-overlay" onMouseDown={() => setDrawerOpen(false)} />
          <div className="drawer">
            <TramiteDetalle tramiteId={tramiteSeleccionado} onClose={() => setDrawerOpen(false)} onUpdate={fetchTramites} />
          </div>
        </>
      )}

      {modalOpen && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalOpen(false)}>
          <div className="modal" style={{ width: 620 }} onKeyDown={e => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'SELECT') saveTramite(); }}>
            <div className="modal-header">
              <div className="modal-title">{editingId ? 'Editar trámite' : 'Nuevo trámite'}</div>
              <button className="modal-close" onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Carta documento, Pedido de informes" />
                </div>
                <div className="form-group form-full">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label className="form-label" style={{ margin: 0 }}>Tipo de trámite</label>
                    <span style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }} onClick={() => setForm(f => ({ ...f, mostrar_tipo: !f.mostrar_tipo, tipo: '' }))}>
                      {form.mostrar_tipo ? 'Quitar' : 'Agregar'}
                    </span>
                  </div>
                  {form.mostrar_tipo && <input className="form-input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} placeholder="Ej: Epistolar, Administrativo" />}
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de inicio</label>
                  <input className="form-input" type="date" value={form.fecha_inicio} onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de vencimiento</label>
                  <input className="form-input" type="date" value={form.fecha_vencimiento} onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select className="form-select" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                    <option value="pendiente">Pendiente</option>
                    <option value="en espera">En espera</option>
                    <option value="completado">Completado</option>
                    <option value="archivado">Archivado</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Expediente vinculado</label>
                  <select className="form-select" value={form.expediente_id} onChange={e => setForm(f => ({ ...f, expediente_id: e.target.value }))}>
                    <option value="">Sin vincular</option>
                    {expedientes.map(e => <option key={e.id} value={e.id}>{e.numero ? `${e.numero} — ` : ''}{e.caratula?.substring(0, 35)}</option>)}
                  </select>
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Clientes vinculados</label>
                  {form.cliente_ids.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                      {form.cliente_ids.map(id => {
                        const c = clientes.find(x => x.id === id);
                        return c ? (
                          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'var(--blue-light)', border: '1px solid var(--blue)', color: 'var(--blue)', fontSize: 12 }}>
                            {c.nombre} {c.apellido}
                            <span onClick={() => toggleCliente(id)} style={{ cursor: 'pointer', fontWeight: 700, marginLeft: 2 }}>×</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-input"
                      placeholder="Buscar cliente por nombre o DNI..."
                      value={busquedaCliente}
                      onChange={e => setBusquedaCliente(e.target.value)}
                      onFocus={() => setDropdownClienteOpen(true)}
                      onBlur={() => setTimeout(() => setDropdownClienteOpen(false), 150)}
                    />
                    {dropdownClienteOpen && (
                      <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 200, maxHeight: 200, overflowY: 'auto', marginBottom: 4, boxShadow: '0 -4px 16px rgba(0,0,0,0.3)' }}>
                        {clientes.filter(c => {
                          const t = busquedaCliente.toLowerCase();
                          return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t) || (c.dni || '').includes(t);
                        }).map(c => (
                          <div key={c.id} onMouseDown={() => { toggleCliente(c.id); setBusquedaCliente(''); }}
                            style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              background: form.cliente_ids.includes(c.id) ? 'var(--blue-light)' : 'transparent',
                              color: form.cliente_ids.includes(c.id) ? 'var(--blue)' : 'var(--text2)' }}>
                            <span>{c.nombre} {c.apellido}{c.dni ? <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 8 }}>{c.dni}</span> : ''}</span>
                            {form.cliente_ids.includes(c.id) && <span style={{ fontSize: 12 }}>✓</span>}
                          </div>
                        ))}
                        {clientes.filter(c => { const t = busquedaCliente.toLowerCase(); return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t) || (c.dni || '').includes(t); }).length === 0 && (
                          <div style={{ padding: '10px 14px', color: 'var(--text4)', fontSize: 13 }}>Sin resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group form-full">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label className="form-label" style={{ margin: 0 }}>Parte contraria</label>
                    <span style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }} onClick={() => setForm(f => ({ ...f, mostrar_parte_contraria: !f.mostrar_parte_contraria, parte_contraria: '' }))}>
                      {form.mostrar_parte_contraria ? 'Quitar' : 'Agregar'}
                    </span>
                  </div>
                  {form.mostrar_parte_contraria && <input className="form-input" value={form.parte_contraria} onChange={e => setForm(f => ({ ...f, parte_contraria: e.target.value }))} placeholder="Nombre de la parte contraria" />}
                </div>
                {form.estado === 'completado' && (
                  <div className="form-group form-full">
                    <label className="form-label">Resultado / Conclusión</label>
                    <textarea className="form-textarea" value={form.resultado} onChange={e => setForm(f => ({ ...f, resultado: e.target.value }))} placeholder="¿Cómo terminó el trámite?" />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              <div>
                {editingId && (
                  <button className="btn btn-danger" onClick={() => { setModalOpen(false); const t = tramites.find(x => x.id === editingId); setModalConfirmOpen({ id: editingId, nombre: t?.nombre || 'este trámite' }); }}>
                    🗑 Eliminar
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button className="btn btn-ghost" onClick={() => setModalOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={saveTramite} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar trámite'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalConfirmOpen && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalConfirmOpen(null)}>
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">⚠️ Atención</div>
              <button className="modal-close" onClick={() => setModalConfirmOpen(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 12 }}>
                Estás por realizar una acción sobre <strong>{modalConfirmOpen.nombre}</strong>.
              </p>
              <div style={{ background: 'var(--red-light)', border: '0.5px solid #fecaca', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--red)', lineHeight: 1.6, marginBottom: 10 }}>
                Eliminar borrará el trámite y todos sus vínculos. <strong>Esta acción no se puede deshacer.</strong>
              </div>
              <div style={{ background: 'var(--blue-light)', border: '0.5px solid var(--blue)', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--blue)', lineHeight: 1.6 }}>
                💡 Recomendamos marcar como <strong>Completado</strong> o <strong>Archivado</strong> para conservar el historial.
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
              {esAdmin && <button className="btn btn-danger" onClick={() => confirmarAccionTramite('eliminar', modalConfirmOpen.id)}>
                🗑 Eliminar definitivamente
              </button>}
              <div className="flex gap-2">
                <button className="btn btn-ghost" onClick={() => setModalConfirmOpen(null)}>Cancelar</button>
                <button className="btn btn-ghost" onClick={() => confirmarAccionTramite('archivado', modalConfirmOpen.id)}>📁 Archivar</button>
                <button className="btn btn-primary" onClick={() => confirmarAccionTramite('completado', modalConfirmOpen.id)}>✓ Marcar completado</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">✓ {toast}</div>}
    </div>
  );
}